import { motion } from 'framer-motion';
import { 
  Users, 
  Heart, 
  Target, 
  Award,
  GraduationCap,
  Briefcase,
  Building2,
  ArrowRight,
  Quote,
  CheckCircle2
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

const IMAGES = {
  team: "https://customer-assets.emergentagent.com/job_dbe93cb4-9c50-4fba-b93b-913745c122fd/artifacts/u0me3lk2_141209352_275139740693455_6138775839380989747_o.jpg",
  recruitment: "https://customer-assets.emergentagent.com/job_dbe93cb4-9c50-4fba-b93b-913745c122fd/artifacts/u0me3lk2_141209352_275139740693455_6138775839380989747_o.jpg"
};

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const teamMembers = [
  {
    name: 'Eric ASPINAS',
    role: 'Fondateur & Directeur',
    description: 'Fort d\'une carrière dans la gendarmerie avec une solide expérience en ressources humaines, management et finances. Il a développé un profond sens de l\'humanité, de l\'écoute et de l\'accompagnement.',
    image: 'https://customer-assets.emergentagent.com/job_dbe93cb4-9c50-4fba-b93b-913745c122fd/artifacts/k8sw905t_Eric.jpeg'
  },
  {
    name: 'Julie Poccard-Chapuis',
    role: 'Responsable Pédagogique',
    description: 'Ancienne directrice d\'un groupement d\'employeurs pour l\'insertion et la qualification professionnelle. Elle développe les programmes de formation et assure leur bon déroulement.',
    image: 'https://customer-assets.emergentagent.com/job_dbe93cb4-9c50-4fba-b93b-913745c122fd/artifacts/t3qe4q2w_Julie.jpeg'
  },
  {
    name: 'Kealii Chong',
    role: 'Assistant Administratif et Commercial',
    description: 'Titulaire d\'un BTS Gestion PME PMI. Il gère la coordination administrative et commerciale au sein du groupe et avec les clients.',
    image: 'https://customer-assets.emergentagent.com/job_dbe93cb4-9c50-4fba-b93b-913745c122fd/artifacts/5cpzi4ou_Kealii.jpeg'
  }
];

const values = [
  {
    icon: Heart,
    title: 'Respect',
    description: 'Une approche humaine et bienveillante dans toutes nos interactions.'
  },
  {
    icon: Users,
    title: 'Entraide',
    description: 'La solidarité et l\'accompagnement au cœur de notre démarche.'
  },
  {
    icon: Target,
    title: 'Rigueur',
    description: 'Un travail de qualité professionnelle et un engagement total.'
  },
  {
    icon: Award,
    title: 'Communauté',
    description: 'Au service du développement local et des talents polynésiens.'
  }
];

const entities = [
  {
    icon: Briefcase,
    name: 'AERH Consulting Tahiti',
    description: 'Conseil en création d\'entreprise, accompagnement RH et développement stratégique',
    website: 'www.groupeaerhpolynesie.com'
  },
  {
    icon: GraduationCap,
    name: 'AERH Formations Tahiti',
    description: 'Formation professionnelle adaptée aux réalités polynésiennes',
    website: 'www.groupeaerhpolynesie.com'
  },
  {
    icon: Building2,
    name: 'AERH Recrutement Tahiti',
    description: 'Identification, sélection et intégration de talents durables',
    website: 'www.groupeaerhpolynesie.com'
  },
  {
    icon: CheckCircle2,
    name: 'Moana Paye',
    description: 'Externalisation de la paie conforme à la réglementation polynésienne',
    website: 'www.groupeaerhpolynesie.com'
  }
];

export const AboutPage = () => {
  return (
    <div className="overflow-hidden" data-testid="about-page">
      {/* Hero Section */}
      <section className="relative py-16 bg-teal-700" data-testid="about-hero">
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <pattern id="tapa" width="20" height="20" patternUnits="userSpaceOnUse">
              <rect width="20" height="20" fill="none"/>
              <path d="M0 10 L10 0 L20 10 L10 20 Z" fill="currentColor" opacity="0.3"/>
            </pattern>
            <rect width="100%" height="100%" fill="url(#tapa)"/>
          </svg>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-8">
          <motion.div 
            className="text-center"
            initial="initial"
            animate="animate"
            variants={fadeInUp}
          >
            <span className="inline-block bg-gold-500/20 text-gold-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
              Notre Histoire
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Qui sommes-nous ?
            </h1>
            <p className="text-xl text-teal-100 max-w-3xl mx-auto">
              Le Groupe AERH Polynésie accompagne les entreprises et les talents locaux 
              à travers quatre métiers complémentaires : consulting, formation, recrutement et solutions paie.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 bg-white" data-testid="story-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-gold-600 font-medium uppercase tracking-wider text-sm">Notre Histoire</span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-3 mb-6">
                Une conviction devenue réalité
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  AERH est né de la conviction d'Eric ASPINAS qu'il faut <strong className="text-teal-700">donner confiance, 
                  révéler les potentiels et ouvrir de nouvelles perspectives</strong> pour les entreprises et les talents de Polynésie.
                </p>
                <p>
                  Le groupe s'est construit autour de quatre métiers indissociables : le <strong className="text-teal-700">consulting</strong> pour 
                  accompagner la création et le développement d'entreprise, la <strong className="text-teal-700">formation</strong> pour renforcer 
                  les compétences, le <strong className="text-teal-700">recrutement</strong> pour identifier et intégrer les bons profils, 
                  et <strong className="text-teal-700">Moana Paye</strong> pour sécuriser la gestion de la paie.
                </p>
                <p>
                  L'équipe s'est construite en s'entourant de "perles rares" – une équipe 
                  intergénérationnelle animée par des valeurs fortes : le respect, l'entraide, 
                  la rigueur et le sens de la communauté.
                </p>
              </div>
              
              <div className="mt-8 p-6 bg-slate-50 rounded-2xl border-l-4 border-gold-500">
                <Quote className="w-8 h-8 text-gold-500 mb-3" />
                <p className="text-slate-700 italic">
                  "Notre mission est d'accompagner les entreprises polynésiennes dans leur croissance — 
                  du conseil stratégique à la gestion de la paie, en passant par la formation et le recrutement."
                </p>
                <p className="mt-3 font-semibold text-teal-700">— Eric ASPINAS, Fondateur</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src={IMAGES.team} 
                  alt="Équipe AERH Polynésie" 
                  className="w-full h-auto"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-gold-500 rounded-2xl -z-10" />
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-teal-100 rounded-full -z-10" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-slate-50" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-gold-600 font-medium uppercase tracking-wider text-sm">Nos Valeurs</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-3">
              Ce qui nous anime
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow text-center"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-teal-100 text-teal-700 mb-6">
                  <value.icon className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">{value.title}</h3>
                <p className="text-slate-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 bg-white" data-testid="team-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-gold-600 font-medium uppercase tracking-wider text-sm">Notre Équipe</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-3 mb-4">
              Une équipe passionnée
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Des professionnels expérimentés au service de votre réussite.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {teamMembers.map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-slate-50 rounded-2xl p-6 hover:bg-white hover:shadow-lg transition-all border border-slate-100"
              >
                {member.image ? (
                  <img src={member.image} alt={member.name} className="w-24 h-24 rounded-full object-cover object-top mb-4" />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-teal-700 text-white flex items-center justify-center text-2xl font-bold mb-4">
                    {member.name.split(' ').map(n => n[0]).join('')}
                  </div>
                )}
                <h3 className="text-xl font-semibold text-slate-900 mb-1">{member.name}</h3>
                <p className="text-gold-600 font-medium mb-3">{member.role}</p>
                <p className="text-slate-600 text-sm">{member.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Entities Section */}
      <section className="py-16 bg-teal-700" data-testid="entities-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-gold-400 font-medium uppercase tracking-wider text-sm">Le Groupe</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-3 mb-4">
              Nos entités
            </h2>
            <p className="text-lg text-teal-100 max-w-2xl mx-auto">
              Quatre métiers complémentaires pour répondre à tous vos besoins.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {entities.map((entity, index) => (
              <motion.div
                key={entity.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:bg-white/20 transition-colors"
              >
                <div className="w-14 h-14 rounded-xl bg-gold-500 text-slate-900 flex items-center justify-center mb-6">
                  <entity.icon className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{entity.name}</h3>
                <p className="text-teal-100 mb-4">{entity.description}</p>
                <p className="text-gold-400 text-sm font-medium">{entity.website}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-50" data-testid="about-cta">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              Un projet ? Parlons-en.
            </h2>
            <p className="text-xl text-slate-600 mb-8">
              Consulting, formation, recrutement ou gestion de la paie — 
              notre équipe est à votre écoute pour construire la solution adaptée à vos besoins.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                asChild
                className="bg-teal-700 hover:bg-teal-800 text-white rounded-full px-8 py-6"
                data-testid="about-cta-formations"
              >
                <Link to="/contact">
                  Nous contacter
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button 
                asChild
                variant="outline"
                className="border-2 border-teal-700 text-teal-700 hover:bg-teal-50 rounded-full px-8 py-6"
                data-testid="about-cta-contact"
              >
                <Link to="/formations">
                  Nos services
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
